import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import { $isHeadingNode, $isQuoteNode } from '@lexical/rich-text';
import { $getNearestBlockElementAncestorOrThrow } from '@lexical/utils';
import { getTranslation } from '@nexelem/utils';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton } from '../ActionButton';
import { ButtonWithMenu } from '../ButtonWithMenu';
import { IconButton } from '../IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis, faStrikethrough, faSubscript, faSuperscript, faTrash } from '@fortawesome/pro-light-svg-icons';

type FormattingDropDownProps = {
  readonly disabled?: boolean;
  readonly editor: LexicalEditor;
};

export const FormattingDropDown = ({ disabled, editor }: FormattingDropDownProps) => {
  const { t: translation } = useTranslation();
  const t = useMemo(() => getTranslation(translation, 'components.richTextEditor.formatting'), [translation]);

  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);

  const clearFormatting = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchor = selection.anchor;
        const focus = selection.focus;
        const nodes = selection.getNodes();

        if (anchor.key === focus.key && anchor.offset === focus.offset) {
          return;
        }

        nodes.forEach((node, idx) => {
          // We split the first and last node by the selection
          // So that we don't format unselected text inside those nodes
          if ($isTextNode(node)) {
            if (idx === 0 && anchor.offset !== 0) {
              node = node.splitText(anchor.offset)[1] || node;
            }
            if (idx === nodes.length - 1) {
              node = node['splitText'](focus.offset)[0] || node;
            }

            if (node['__style'] !== '') {
              node['setStyle']('');
            }
            if (node['__format'] !== 0) {
              node['setFormat'](0);
              $getNearestBlockElementAncestorOrThrow(node).setFormat('');
            }
          } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
            node.replace($createParagraphNode(), true);
          } else if ($isDecoratorBlockNode(node)) {
            node.setFormat('');
          }
        });
      }
    });
  }, [editor]);

  const updateFormatting = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsSuperscript(selection.hasFormat('superscript'));
      setIsSubscript(selection.hasFormat('subscript'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
    }
  }, []);

  const onClickStrikethrough = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
  }, [editor]);

  const onClickSubscript = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
  }, [editor]);

  const onClickSuperscript = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
  }, [editor]);

  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateFormatting();
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    [editor, updateFormatting],
  );

  useEffect(() => {
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateFormatting();
      });
    });
  }, [editor, updateFormatting]);

  return (
    <ButtonWithMenu
      disabled={disabled}
      openingElement={<IconButton StartIcon={<FontAwesomeIcon icon={faEllipsis} />} variant="text" />}
      content={() => (
        <>
          <ActionButton
            ariaLabel={t('strikethrough.ariaLabel')}
            Icon={<FontAwesomeIcon icon={faStrikethrough} />}
            isActive={isStrikethrough}
            label={t('strikethrough.label')}
            onClick={onClickStrikethrough}
          />
          <ActionButton
            ariaLabel={t('subscript.ariaLabel')}
            disabled={isSuperscript}
            Icon={<FontAwesomeIcon icon={faSubscript} />}
            isActive={isSubscript}
            label={t('subscript.label')}
            onClick={onClickSubscript}
          />
          <ActionButton
            ariaLabel={t('superscript.ariaLabel')}
            disabled={isSubscript}
            Icon={<FontAwesomeIcon icon={faSuperscript} />}
            isActive={isSuperscript}
            label={t('superscript.label')}
            onClick={onClickSuperscript}
          />
          <ActionButton
            ariaLabel={t('clearFormatting.ariaLabel')}
            Icon={<FontAwesomeIcon icon={faTrash} />}
            label={t('clearFormatting.label')}
            onClick={clearFormatting}
          />
        </>
      )}
    />
  );
};
FormattingDropDown.displayName = 'FormattingDropDown';
